import type { DraftState, Manager, ManagerId, TradeEntry } from '../types';
import { DEFAULT_MANAGERS, ALL_TEAM_ABBRS } from '../constants/teams';

export const ROSTER_CAP = 8;
export const MIN_BID = 1;

export interface InitialDraftOptions {
  managers?: Manager[];
  leagueId?: string;
  leagueName?: string;
  commissionerIds?: ManagerId[];
  rosterCap?: number;
  minBid?: number;
}

export function buildInitialDraftState(year: number, opts: InitialDraftOptions = {}): DraftState {
  const baseManagers = opts.managers && opts.managers.length > 0 ? opts.managers : DEFAULT_MANAGERS;
  return {
    managers: baseManagers.map(m => ({ ...m, teams: [], teamsDrafted: 0, budget: m.initialBudget })),
    availableTeams: [...ALL_TEAM_ABBRS],
    currentNomineeIndex: 0,
    phase: 'Nomination',
    auction: {
      team: null,
      highBid: 0,
      highBidderId: null,
      passers: [],
      nominatorId: null,
      startedAt: null,
    },
    history: [],
    trades: [],
    isDraftFinished: false,
    currentDraftYear: year,
    rosterCap: opts.rosterCap ?? ROSTER_CAP,
    minBid: opts.minBid ?? MIN_BID,
    leagueId: opts.leagueId,
    leagueName: opts.leagueName,
    commissionerIds: opts.commissionerIds,
  };
}

/**
 * Fill in any missing/invalid fields on a state doc loaded from Firestore.
 * Self-heals docs written by older versions of the app.
 */
export function normalizeDraftState(raw: any, fallbackYear: number): DraftState {
  const s: any = raw ?? {};
  const draftedSet = new Set<string>();
  const managers: Manager[] = Array.isArray(s.managers) ? s.managers.map((m: any) => {
    const teams: string[] = Array.isArray(m?.teams) ? m.teams : [];
    teams.forEach(t => draftedSet.add(t));
    const initialBudget = Number.isFinite(m?.initialBudget) ? m.initialBudget : (Number.isFinite(m?.budget) ? m.budget : 50);
    return {
      id: String(m?.id ?? ''),
      name: String(m?.name ?? m?.id ?? ''),
      emoji: m?.emoji,
      initialBudget,
      budget: Number.isFinite(m?.budget) ? m.budget : initialBudget,
      teams,
      teamsDrafted: Number.isFinite(m?.teamsDrafted) ? m.teamsDrafted : teams.length,
    };
  }) : DEFAULT_MANAGERS.map(m => ({ ...m, teams: [], teamsDrafted: 0, budget: m.initialBudget }));

  const availableTeams: string[] = Array.isArray(s.availableTeams) && s.availableTeams.length > 0
    ? s.availableTeams
    : ALL_TEAM_ABBRS.filter(t => !draftedSet.has(t));

  const a = s.auction ?? {};
  return {
    managers,
    availableTeams,
    currentNomineeIndex: Number.isFinite(s.currentNomineeIndex) ? s.currentNomineeIndex : 0,
    phase: s.phase === 'Bidding' || s.phase === 'Finished' ? s.phase : 'Nomination',
    auction: {
      team: a.team ?? null,
      highBid: Number.isFinite(a.highBid) ? a.highBid : 0,
      highBidderId: a.highBidderId ?? null,
      passers: Array.isArray(a.passers) ? a.passers : [],
      nominatorId: a.nominatorId ?? null,
      startedAt: Number.isFinite(a.startedAt) ? a.startedAt : null,
    },
    history: Array.isArray(s.history) ? s.history : [],
    trades: Array.isArray(s.trades) ? s.trades : [],
    isDraftFinished: !!s.isDraftFinished,
    currentDraftYear: Number.isFinite(s.currentDraftYear) ? s.currentDraftYear : fallbackYear,
    rosterCap: Number.isFinite(s.rosterCap) && s.rosterCap > 0 ? s.rosterCap : ROSTER_CAP,
    minBid: Number.isFinite(s.minBid) && s.minBid > 0 ? s.minBid : MIN_BID,
    leagueId: typeof s.leagueId === 'string' ? s.leagueId : undefined,
    leagueName: typeof s.leagueName === 'string' ? s.leagueName : undefined,
    commissionerIds: Array.isArray(s.commissionerIds)
      ? s.commissionerIds.filter((x: unknown): x is string => typeof x === 'string')
      : undefined,
  };
}

export function findManager(state: DraftState, id: ManagerId | null | undefined): Manager | undefined {
  if (!id) return undefined;
  return state.managers.find(m => m.id === id);
}

// Max safe bid = budget - (teams_remaining - 1) * MIN_BID
// You must reserve $1 for each future roster slot beyond the one you're bidding on now.
export function maxBidPower(state: DraftState, managerId: ManagerId): number {
  const m = findManager(state, managerId);
  if (!m) return 0;
  const teamsRemaining = state.rosterCap - m.teamsDrafted;
  if (teamsRemaining <= 0) return 0;
  const reserve = Math.max(0, teamsRemaining - 1) * state.minBid;
  return Math.max(0, m.budget - reserve);
}

export function teamsRemainingFor(state: DraftState, managerId: ManagerId): number {
  const m = findManager(state, managerId);
  if (!m) return 0;
  return Math.max(0, state.rosterCap - m.teamsDrafted);
}

// Determine the next manager whose turn it is to act during bidding.
// Rules:
//  - Skip the current high bidder (they're already "winning")
//  - Skip anyone in the passers list
//  - Skip anyone whose roster is full
//  - Skip anyone who cannot afford the next required bid
//  - Rotate starting AFTER the most recent actor (high bidder)
export function nextBidderId(state: DraftState): ManagerId | null {
  const a = state.auction;
  if (!a || !a.highBidderId) return null;
  const ids = state.managers.map(m => m.id);
  const startIdx = ids.indexOf(a.highBidderId);
  const nextRequired = a.highBid + 1;

  for (let i = 1; i <= ids.length; i++) {
    const idx = (startIdx + i) % ids.length;
    const candidateId = ids[idx];
    if (candidateId === a.highBidderId) continue;
    const m = state.managers[idx];
    if (m.teamsDrafted >= state.rosterCap) continue;
    if (a.passers.includes(candidateId)) continue;
    if (maxBidPower(state, candidateId) < nextRequired) continue;
    return candidateId;
  }
  return null; // everyone passed/can't afford — auction ready to be sold
}

// Returns true if everyone other than high bidder has passed/can't afford -> ready to sell
export function isReadyToSell(state: DraftState): boolean {
  if (state.phase !== 'Bidding') return false;
  return nextBidderId(state) === null;
}

// Find the next nominator index, skipping managers whose rosters are full.
export function nextNomineeIndex(state: DraftState, fromIdx: number): number {
  const N = state.managers.length;
  for (let i = 1; i <= N; i++) {
    const idx = (fromIdx + i) % N;
    if (state.managers[idx].teamsDrafted < state.rosterCap) return idx;
  }
  return fromIdx;
}

export function allRostersFull(state: DraftState): boolean {
  return state.managers.every(m => m.teamsDrafted >= state.rosterCap);
}

// ---- State transitions (pure; return new DraftState) ----

export function startAuction(
  state: DraftState,
  team: string,
  initialBid: number,
  nominatorId: ManagerId,
): DraftState {
  return {
    ...state,
    phase: 'Bidding',
    availableTeams: state.availableTeams.filter(t => t !== team),
    auction: {
      team,
      highBid: initialBid,
      highBidderId: nominatorId,
      passers: [],
      nominatorId,
      startedAt: Date.now(),
    },
  };
}

export function placeBid(state: DraftState, bidderId: ManagerId, amount: number): DraftState {
  return {
    ...state,
    auction: {
      ...state.auction,
      highBid: amount,
      highBidderId: bidderId,
      // Do NOT reset passers — once you pass, you're out for this auction
    },
  };
}

export function recordPass(state: DraftState, passerId: ManagerId): DraftState {
  const passers = state.auction.passers.includes(passerId)
    ? state.auction.passers
    : [...state.auction.passers, passerId];
  return { ...state, auction: { ...state.auction, passers } };
}

export function finalizeSale(state: DraftState): DraftState {
  const { auction } = state;
  if (!auction.team || !auction.highBidderId) return state;
  const winnerIdx = state.managers.findIndex(m => m.id === auction.highBidderId);
  if (winnerIdx < 0) return state;
  const winner = state.managers[winnerIdx];

  const newManagers = state.managers.map((m, i) =>
    i === winnerIdx
      ? {
          ...m,
          budget: m.budget - auction.highBid,
          teams: [...m.teams, auction.team!],
          teamsDrafted: m.teamsDrafted + 1,
        }
      : m,
  );

  const newHistory = [
    {
      team: auction.team!,
      winner: winner.name,
      winnerId: winner.id,
      price: auction.highBid,
      round: state.history.length + 1,
      ts: Date.now(),
    },
    ...state.history,
  ];

  const isFinished = newManagers.every(m => m.teamsDrafted >= state.rosterCap);
  const next = isFinished
    ? state.currentNomineeIndex
    : nextNomineeIndex({ ...state, managers: newManagers }, state.currentNomineeIndex);

  return {
    ...state,
    managers: newManagers,
    history: newHistory,
    currentNomineeIndex: next,
    phase: isFinished ? 'Finished' : 'Nomination',
    isDraftFinished: isFinished,
    auction: {
      team: null,
      highBid: 0,
      highBidderId: null,
      passers: [],
      nominatorId: null,
      startedAt: null,
    },
  };
}

export function cancelAuction(state: DraftState): DraftState {
  const teamBack = state.auction.team;
  return {
    ...state,
    phase: 'Nomination',
    availableTeams: teamBack ? [teamBack, ...state.availableTeams] : state.availableTeams,
    auction: {
      team: null, highBid: 0, highBidderId: null, passers: [], nominatorId: null, startedAt: null,
    },
  };
}

// ---- Trades (post-draft, between any two managers) ----

export interface TradeSpec {
  managerAId: ManagerId;
  managerBId: ManagerId;
  teamsFromA: string[];
  teamsFromB: string[];
  cashFromA?: number;
  cashFromB?: number;
  note?: string;
}

export function validateTrade(state: DraftState, spec: TradeSpec): string | null {
  if (spec.managerAId === spec.managerBId) return 'Choose two different managers.';
  const a = findManager(state, spec.managerAId);
  const b = findManager(state, spec.managerBId);
  if (!a || !b) return 'Manager not found.';
  if (spec.teamsFromA.length === 0 && spec.teamsFromB.length === 0 && !spec.cashFromA && !spec.cashFromB) {
    return 'Trade must include at least one team or cash on either side.';
  }
  for (const t of spec.teamsFromA) {
    if (!a.teams.includes(t)) return `${a.name} does not own ${t}.`;
  }
  for (const t of spec.teamsFromB) {
    if (!b.teams.includes(t)) return `${b.name} does not own ${t}.`;
  }
  if (spec.cashFromA && spec.cashFromA > a.budget) return `${a.name} only has $${a.budget}.`;
  if (spec.cashFromB && spec.cashFromB > b.budget) return `${b.name} only has $${b.budget}.`;
  return null;
}

export function executeTrade(state: DraftState, spec: TradeSpec): DraftState {
  const err = validateTrade(state, spec);
  if (err) throw new Error(err);
  const a = findManager(state, spec.managerAId)!;
  const b = findManager(state, spec.managerBId)!;
  const cashA = spec.cashFromA ?? 0;
  const cashB = spec.cashFromB ?? 0;

  const newManagers = state.managers.map(m => {
    if (m.id === a.id) {
      const teams = [
        ...m.teams.filter(t => !spec.teamsFromA.includes(t)),
        ...spec.teamsFromB,
      ];
      return { ...m, teams, teamsDrafted: teams.length, budget: m.budget - cashA + cashB };
    }
    if (m.id === b.id) {
      const teams = [
        ...m.teams.filter(t => !spec.teamsFromB.includes(t)),
        ...spec.teamsFromA,
      ];
      return { ...m, teams, teamsDrafted: teams.length, budget: m.budget - cashB + cashA };
    }
    return m;
  });

  const trade: TradeEntry = {
    id: `trade-${Date.now()}`,
    ts: Date.now(),
    year: state.currentDraftYear,
    managerAId: a.id,
    managerAName: a.name,
    managerBId: b.id,
    managerBName: b.name,
    teamsFromA: spec.teamsFromA,
    teamsFromB: spec.teamsFromB,
    cashFromA: cashA || undefined,
    cashFromB: cashB || undefined,
    note: spec.note,
  };

  return {
    ...state,
    managers: newManagers,
    trades: [trade, ...(state.trades ?? [])],
  };
}

export function undoLastTrade(state: DraftState): DraftState {
  const trades = state.trades ?? [];
  if (trades.length === 0) return state;
  const [last, ...rest] = trades;
  const newManagers = state.managers.map(m => {
    if (m.id === last.managerAId) {
      const teams = [
        ...m.teams.filter(t => !last.teamsFromB.includes(t)),
        ...last.teamsFromA,
      ];
      return {
        ...m, teams, teamsDrafted: teams.length,
        budget: m.budget + (last.cashFromA ?? 0) - (last.cashFromB ?? 0),
      };
    }
    if (m.id === last.managerBId) {
      const teams = [
        ...m.teams.filter(t => !last.teamsFromA.includes(t)),
        ...last.teamsFromB,
      ];
      return {
        ...m, teams, teamsDrafted: teams.length,
        budget: m.budget + (last.cashFromB ?? 0) - (last.cashFromA ?? 0),
      };
    }
    return m;
  });
  return { ...state, managers: newManagers, trades: rest };
}

export function undoLastPick(state: DraftState): DraftState {
  if (state.history.length === 0) return state;
  const [last, ...rest] = state.history;
  const idx = state.managers.findIndex(m => m.id === last.winnerId);
  if (idx < 0) return state;
  const newManagers = state.managers.map((m, i) =>
    i === idx
      ? {
          ...m,
          budget: m.budget + last.price,
          teams: m.teams.filter(t => t !== last.team),
          teamsDrafted: Math.max(0, m.teamsDrafted - 1),
        }
      : m,
  );
  return {
    ...state,
    managers: newManagers,
    history: rest,
    availableTeams: [last.team, ...state.availableTeams],
    currentNomineeIndex: idx,
    phase: 'Nomination',
    isDraftFinished: false,
    auction: { team: null, highBid: 0, highBidderId: null, passers: [], nominatorId: null, startedAt: null },
  };
}
